import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Palette,
  Upload,
  Trash2,
  Loader2,
  Image as ImageIcon,
  Type,
  Lock,
  Sparkles,
  Info,
  LayoutTemplate,
  MousePointerClick,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BRANDING = {
  logo_url: null,
  primary_color: "#1a4d2e",
  background_color: "#ffffff",
  button_color: "#1a4d2e",
  text_color: "#1a1a1a",
  welcome_message: null,
  thank_you_message: null,
  show_logo: true,
  show_business_name: true,
  font_family: "default",
  button_style: "rounded",
  submit_button_text: "Submit Application",
};

const FONT_OPTIONS = [
  { value: "default", label: "Default (HeyIntake)", stack: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
  { value: "serif", label: "Serif (Editorial)", stack: "'Playfair Display', 'Libre Baskerville', Georgia, serif" },
  { value: "modern_sans", label: "Modern Sans", stack: "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { value: "elegant_sans", label: "Elegant Sans", stack: "'Outfit', 'Plus Jakarta Sans', system-ui, sans-serif" },
];

const fontStack = (key) => (FONT_OPTIONS.find((f) => f.value === key) || FONT_OPTIONS[0]).stack;

const BUTTON_RADIUS = {
  rounded: "9999px",
  soft: "10px",
  square: "2px",
};

export default function FormBrandingSettings({
  formId,
  token,
  branding: externalBranding,
  onBrandingChange,
  onDirty,
  brandingEnabled,
  onBrandingEnabledChange,
  businessName,
}) {
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [branding, setBrandingLocal] = useState(DEFAULT_BRANDING);
  const [hasAccess, setHasAccess] = useState(false);
  const [planRequired, setPlanRequired] = useState(null);
  const fileInputRef = useRef(null);
  const initializedRef = useRef(false);

  const branding_data = { ...DEFAULT_BRANDING, ...(externalBranding || branding || {}) };

  useEffect(() => {
    if (formId) fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/business/forms/${formId}/branding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetched = { ...DEFAULT_BRANDING, ...(response.data.branding || {}) };
      setBrandingLocal(fetched);
      if (onBrandingChange && !initializedRef.current) {
        onBrandingChange(fetched);
        initializedRef.current = true;
      }
      setHasAccess(response.data.custom_branding_enabled);
      setPlanRequired(response.data.plan_required);
    } catch (error) {
      console.error("Failed to fetch branding");
    } finally {
      setLoading(false);
    }
  };

  const updateBranding = (key, value) => {
    const updated = { ...branding_data, [key]: value };
    setBrandingLocal(updated);
    if (onBrandingChange) onBrandingChange(updated);
    if (onDirty) onDirty();
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("File too large. Max size: 2MB");
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) return toast.error("Invalid file type. Allowed: PNG, JPEG, WebP, SVG");

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${API}/business/forms/${formId}/branding/logo`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        },
      );
      const updated = { ...branding_data, logo_url: response.data.logo_url };
      setBrandingLocal(updated);
      if (onBrandingChange) onBrandingChange(updated);
      if (onDirty) onDirty();
      toast.success("Logo uploaded!");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Upgrade to Professional plan to upload logos");
      } else {
        toast.error(error.response?.data?.detail || "Failed to upload logo");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/business/forms/${formId}/branding/logo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updated = { ...branding_data, logo_url: null };
      setBrandingLocal(updated);
      if (onBrandingChange) onBrandingChange(updated);
      if (onDirty) onDirty();
      toast.success("Logo removed");
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <Card className="rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-2">Custom Branding</h3>
          <p className="text-muted-foreground mb-4 max-w-md mx-auto">
            Personalize your intake forms with your logo, brand colors, typography, and more.
            Available on Professional and Enterprise plans.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <Badge variant="outline" className="bg-white"><ImageIcon className="w-3 h-3 mr-1" />Custom Logo</Badge>
            <Badge variant="outline" className="bg-white"><Palette className="w-3 h-3 mr-1" />Brand Colors</Badge>
            <Badge variant="outline" className="bg-white"><Type className="w-3 h-3 mr-1" />Typography</Badge>
          </div>
          <Button className="rounded-full" onClick={() => (window.location.href = "/settings")} data-testid="upgrade-branding-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Professional
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isOverrideMode = brandingEnabled !== false;

  const ButtonStyleCard = ({ value, label, active, onClick, testId }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex flex-col items-center gap-3 rounded-xl border p-4 transition-all ${
        active
          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
          : "border-zinc-200 hover:border-zinc-300 bg-white"
      }`}
    >
      <div
        className="px-5 py-2 text-white text-xs font-medium"
        style={{ backgroundColor: branding_data.button_color, borderRadius: BUTTON_RADIUS[value] }}
      >
        Button
      </div>
      <span className="text-xs font-medium text-zinc-700">{label}</span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Inheritance Banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
        <Info className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-600">
            {isOverrideMode
              ? "This form uses custom branding. Turn off to inherit from Settings."
              : "This form uses your default business branding from Settings."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-slate-500">{isOverrideMode ? "Custom" : "Inherited"}</span>
          <Switch
            checked={isOverrideMode}
            onCheckedChange={(val) => {
              if (onBrandingEnabledChange) onBrandingEnabledChange(val);
              if (onDirty) onDirty();
            }}
            data-testid="branding-override-toggle"
          />
        </div>
      </div>

      {!isOverrideMode && (
        <div className="text-center py-8 text-slate-400">
          <Palette className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">
            Branding inherited from <a href="/settings?section=branding" className="text-primary underline">Settings</a>
          </p>
        </div>
      )}

      {isOverrideMode && (
        <>
          {/* ─── HEADER ─── */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-primary" />Form Header</CardTitle>
              <CardDescription>Choose what appears at the top of your form.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">Display Logo in Header</Label>
                  <p className="text-xs text-muted-foreground">{branding_data.logo_url ? "Your uploaded logo" : "Upload a logo below to enable"}</p>
                </div>
                <Switch
                  checked={branding_data.show_logo !== false}
                  onCheckedChange={(v) => updateBranding("show_logo", v)}
                  data-testid="show-logo-toggle"
                />
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <div>
                  <Label className="text-sm">Display Business Name in Header</Label>
                  <p className="text-xs text-muted-foreground">{businessName || "Your business name"}</p>
                </div>
                <Switch
                  checked={branding_data.show_business_name !== false}
                  onCheckedChange={(v) => updateBranding("show_business_name", v)}
                  data-testid="show-business-name-toggle"
                />
              </div>
              {branding_data.show_logo === false && branding_data.show_business_name === false && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2">
                  Both toggles are off — the header will be removed from the form entirely.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ─── TYPOGRAPHY ─── */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Type className="w-5 h-5 text-primary" />Typography</CardTitle>
              <CardDescription>The font applies to titles, labels, and body copy on the public form.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Font Family</Label>
                <Select
                  value={branding_data.font_family || "default"}
                  onValueChange={(v) => updateBranding("font_family", v)}
                >
                  <SelectTrigger data-testid="font-family-select" className="w-full md:w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map((f) => (
                      <SelectItem key={f.value} value={f.value} data-testid={`font-${f.value}`}>
                        <span style={{ fontFamily: f.stack }}>{f.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div
                  className="mt-3 px-4 py-3 rounded-lg bg-zinc-50 border"
                  style={{ fontFamily: fontStack(branding_data.font_family) }}
                  data-testid="font-sample"
                >
                  <div className="text-lg font-semibold text-zinc-900">Aa — Tell us about your project</div>
                  <div className="text-sm text-zinc-500 mt-1">The quick brown fox jumps over the lazy dog.</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ─── LOGO ─── */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ImageIcon className="w-5 h-5 text-primary" />Logo</CardTitle>
              <CardDescription>Square image, PNG or SVG, max 2MB.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30 overflow-hidden">
                  {branding_data.logo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${branding_data.logo_url}`} alt="Logo" className="w-full h-full object-contain p-2" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-muted-foreground/30" />
                  )}
                </div>
                <div className="flex-1">
                  <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="rounded-full" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {branding_data.logo_url ? "Change Logo" : "Upload Logo"}
                    </Button>
                    {branding_data.logo_url && (
                      <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleDeleteLogo} data-testid="delete-logo-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ─── COLORS ─── */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Palette className="w-5 h-5 text-primary" />Brand Colors</CardTitle>
              <CardDescription>Customize the colors to match your brand identity.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: "primary_color", label: "Primary Color", id: "primary-color", default: "#1a4d2e" },
                  { key: "button_color", label: "Button Color", id: "button-color", default: "#1a4d2e" },
                  { key: "background_color", label: "Background", id: "bg-color", default: "#ffffff" },
                  { key: "text_color", label: "Text Color", id: "text-color", default: "#1a1a1a" },
                ].map(({ key, label, id, default: def }) => (
                  <div key={key} className="space-y-2">
                    <Label>{label}</Label>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg border border-border cursor-pointer overflow-hidden" onClick={() => document.getElementById(id)?.click()}>
                        <input id={id} type="color" value={branding_data[key] || def} onChange={(e) => updateBranding(key, e.target.value)} className="w-12 h-12 -m-1 cursor-pointer" data-testid={`${id}-input`} />
                      </div>
                      <Input value={branding_data[key] || def} onChange={(e) => updateBranding(key, e.target.value)} className="font-mono text-sm" maxLength={7} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ─── BUTTONS ─── */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><MousePointerClick className="w-5 h-5 text-primary" />Buttons & CTA</CardTitle>
              <CardDescription>How submit and action buttons appear.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="mb-2 block">Button Style</Label>
                <div className="grid grid-cols-3 gap-3 max-w-md">
                  <ButtonStyleCard value="rounded" label="Rounded" active={branding_data.button_style === "rounded"} onClick={() => updateBranding("button_style", "rounded")} testId="button-style-rounded" />
                  <ButtonStyleCard value="soft" label="Soft" active={branding_data.button_style === "soft"} onClick={() => updateBranding("button_style", "soft")} testId="button-style-soft" />
                  <ButtonStyleCard value="square" label="Square" active={branding_data.button_style === "square"} onClick={() => updateBranding("button_style", "square")} testId="button-style-square" />
                </div>
              </div>
              <div className="space-y-2 max-w-md">
                <Label>Submit Button Text</Label>
                <Input
                  value={branding_data.submit_button_text || "Submit Application"}
                  onChange={(e) => updateBranding("submit_button_text", e.target.value)}
                  placeholder="Submit Application"
                  maxLength={40}
                  data-testid="submit-button-text-input"
                />
                <p className="text-xs text-muted-foreground">Default: “Submit Application”.</p>
              </div>
            </CardContent>
          </Card>

          {/* ─── PREVIEW ─── */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">Preview · Full Form</CardTitle>
              <CardDescription>How applicants will see your form with the current branding.</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="rounded-xl border p-8 md:p-10"
                style={{
                  backgroundColor: branding_data.background_color || "#ffffff",
                  color: branding_data.text_color || "#1a1a1a",
                  fontFamily: fontStack(branding_data.font_family),
                }}
                data-testid="branding-preview"
              >
                {/* Header — shown when at least one of logo/name is ON */}
                {(branding_data.show_logo !== false || branding_data.show_business_name !== false) && (
                  <div className="flex flex-col items-center mb-8">
                    {branding_data.show_logo !== false && (
                      branding_data.logo_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${branding_data.logo_url}`} alt="Logo" className="h-12 object-contain mb-3" />
                      ) : (
                        <div className="h-12 w-12 rounded-md bg-zinc-200 mb-3" />
                      )
                    )}
                    {branding_data.show_business_name !== false && (
                      <div className="text-lg font-semibold tracking-tight">{businessName || "Your Business Name"}</div>
                    )}
                  </div>
                )}
                {/* Body */}
                <div className="max-w-md mx-auto space-y-5">
                  <div>
                    <h3 className="text-2xl font-semibold mb-1" style={{ color: branding_data.primary_color }}>Tell us about your project</h3>
                    <p className="text-sm opacity-70">A few details so we can route your inquiry to the right team.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Full Name</Label>
                    <Input placeholder="Jane Doe" disabled className="bg-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Email</Label>
                    <Input placeholder="jane@studio.com" disabled className="bg-white" />
                  </div>
                  <button
                    type="button"
                    disabled
                    className="w-full mt-2 px-5 py-2.5 text-white text-sm font-medium"
                    style={{
                      backgroundColor: branding_data.button_color,
                      borderRadius: BUTTON_RADIUS[branding_data.button_style] || "9999px",
                    }}
                    data-testid="preview-submit-button"
                  >
                    {branding_data.submit_button_text || "Submit Application"}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Note about removed messages */}
          <div className="text-xs text-muted-foreground px-1">
            Welcome and confirmation messages can be managed in the <strong>Fields</strong> tab (Form Introduction and Thank You Message).
          </div>
        </>
      )}
    </div>
  );
}
